import { DataSource } from "@angular/cdk/collections";
import { ClaimItReferral } from "../models/claim-it-referral.model";
import { OpportunityService } from "../services/opportunity.service";
import {
  exhaustMap,
  map,
  merge,
  Observable,
  startWith,
  Subject,
  takeUntil,
  tap,
  timer
} from "rxjs";
import { Page } from "../models/page.model";

export class AllReferralDatasource extends DataSource<ClaimItReferral> {
  private onDestroy$: Subject<void> = new Subject();
  private onChange$: Subject<void> = new Subject();
  private filters: { [key: string]: string } = {};
  private readonly timerInterval: number = -1;
  public sortField: string | null = null;
  public sortOrder: string | null = null;
  public pageNumber: number = 0;
  public pageSize: number = 10;
  public username: string | null;
  public totalEntries: number = 0;
  public numberOfElements: number = 0;
  public showClaimed: boolean = true;
  public showExpiration: boolean = false;

  constructor(
    private opportunityService: OpportunityService,
    sortField: string | null,
    sortOrder: string | null,
    pageNumber: number,
    pageSize: number,
    filters: { [key: string]: string } = {},
    username: string | null,
    showClaimed: boolean,
    showExpiration: boolean,
    timerInterval?: number | undefined
  ) {
    super();

    this.sortField = sortField;
    this.sortOrder = sortOrder;
    this.pageNumber = pageNumber;
    this.pageSize = pageSize;
    this.filters = filters;
    this.username = username;
    this.showClaimed = showClaimed;
    this.showExpiration = showExpiration;

    // timerInterval is optional. If timerInterval is set to a positive number the datasource will be refreshed every X
    // milliseconds where X is the timerInterval. If timerInterval is negative or undefined only one datasource request
    // will occur.
    this.timerInterval = timerInterval != null ? timerInterval : -1;
  }

  connect(): Observable<ClaimItReferral[]> {
    const request$ = () =>
      this.opportunityService
        .getAllOpportunities(
          this.sortField,
          this.sortOrder,
          this.pageNumber,
          this.pageSize,
          {
            ...this.filters,
            ...(this.showClaimed ? {} : { marketplaceStatus: "ACTIVE" })
          },
          this.username,
          this.showExpiration
        )
        .pipe(
          tap((page: Page<ClaimItReferral>) => {
            this.totalEntries = page.page.totalElements;
            this.numberOfElements =
              page.page.totalPages == 0 || page.page.number + 1 == page.page.totalPages
                ? page.page.totalElements - page.page.size * page.page.number
                : page.page.size;
          }),
          map((page) => page.content)
        );

    // Manual refreshes (sort/filter/paging/etc)
    const manualRefresh$ = this.onChange$;

    if (this.timerInterval >= 0) {
      // Timer-driven polling + manual triggers, but with ONLY ONE HTTP in flight at a time
      const polling$ = timer(0, this.timerInterval);

      const triggers$ = merge(polling$, manualRefresh$);

      return triggers$.pipe(
        takeUntil(this.onDestroy$),
        exhaustMap(() => request$())
      );
    }

    // No interval: do an initial request, then only refresh on user-driven changes
    return manualRefresh$.pipe(
      startWith(void 0), // initial load
      takeUntil(this.onDestroy$),
      exhaustMap(() => request$())
    );
  }

  disconnect(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
    this.onChange$.complete();
  }

  updatePage(nextPage: number): void {
    if (
      (this.pageNumber + nextPage) * this.pageSize < this.totalEntries &&
      (this.pageNumber + nextPage) * this.pageSize >= 0
    ) {
      this.pageNumber += nextPage;
      this.onChange$.next();
    }
  }

  setSortField(field: string): void {
    const switching = this.sortField !== field;
    this.sortField = field;
    this.sortOrder = switching
      ? field === "marketplaceExpirationDate"
        ? "asc"
        : "desc"
      : this.sortOrder === "asc"
        ? "desc"
        : "asc";

    this.onChange$.next();
  }

  sort(): void {
    this.sortOrder = this.sortOrder === "desc" ? "asc" : "desc";
    this.onChange$.next();
  }

  filter(filters: { [key: string]: string }): void {
    this.filters = filters;
    this.pageNumber = 0;
    this.onChange$.next();
  }

  setUsername(username: string | null): void {
    this.username = username;
    this.onChange$.next();
  }

  toggleShowClaimed() {
    this.showClaimed = !this.showClaimed;

    if (this.showClaimed) {
      delete this.filters["marketplaceStatus"];
    } else {
      this.filters["marketplaceStatus"] = "ACTIVE";
    }

    this.pageNumber = 0;
    this.onChange$.next();
  }
}
